import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useMutation, useQuery } from "urql";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { DestructiveConfirmationAlertDialog } from "@/components/ui/destructive-confirmation-alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { TournamentLayoutsQuery, TournamentPhase } from "@/gen/graphql";
import { useTabSearchParams } from "@/hooks/useTabSearchParams";
import { useLimitOffsetPagination } from "@/utils/pagination";

import {
    createTournamentLayoutMutation,
    deleteTournamentLayoutMutation,
    tournamentLayoutsQuery,
} from "./queries/tournamentLayouts";

interface TournamentContext {
    tournament: {
        id: string;
        title: string;
        startAt?: string | null;
        endAt?: string | null;
    };
    refetch: () => void;
}

function getActivePhase(startAt?: string | null, endAt?: string | null): TournamentPhase | null {
    if (!startAt || !endAt) return null;

    const now = new Date();
    const start = new Date(startAt);
    const end = new Date(endAt);

    if (now < start) return "BEFORE";
    if (now > end) return "AFTER";
    return "DURING";
}

export default function TournamentLayoutsRoute() {
    const { tournamentId } = useParams<{ tournamentId: string }>();
    const { tournament } = useOutletContext<TournamentContext>();
    const { tab, setTab } = useTabSearchParams();

    const activePhase = getActivePhase(tournament.startAt, tournament.endAt);

    return (
        <div className="m-4 flex flex-col gap-4">
            <PageHeader title={`Layouts for ${tournament.title}`} />

            <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="before">
                        <span className="flex items-center gap-2">
                            Before
                            {activePhase === "BEFORE" && (
                                <span className="rounded bg-green-500 px-2 py-1 text-xs text-white">
                                    Active
                                </span>
                            )}
                        </span>
                    </TabsTrigger>
                    <TabsTrigger value="during">
                        <span className="flex items-center gap-2">
                            During
                            {activePhase === "DURING" && (
                                <span className="rounded bg-green-500 px-2 py-1 text-xs text-white">
                                    Active
                                </span>
                            )}
                        </span>
                    </TabsTrigger>
                    <TabsTrigger value="after">
                        <span className="flex items-center gap-2">
                            After
                            {activePhase === "AFTER" && (
                                <span className="rounded bg-green-500 px-2 py-1 text-xs text-white">
                                    Active
                                </span>
                            )}
                        </span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="before">
                    <PhaseLayoutsSection
                        tournamentId={tournamentId!}
                        phase={"BEFORE"}
                        phaseLabel="Before Tournament"
                        isActive={activePhase === "BEFORE"}
                    />
                </TabsContent>

                <TabsContent value="during">
                    <PhaseLayoutsSection
                        tournamentId={tournamentId!}
                        phase={"DURING"}
                        phaseLabel="During Tournament"
                        isActive={activePhase === "DURING"}
                    />
                </TabsContent>

                <TabsContent value="after">
                    <PhaseLayoutsSection
                        tournamentId={tournamentId!}
                        phase={"AFTER"}
                        phaseLabel="After Tournament"
                        isActive={activePhase === "AFTER"}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function PhaseLayoutsSection({
    tournamentId,
    phase,
    phaseLabel,
    isActive,
}: {
    tournamentId: string;
    phase: TournamentPhase;
    phaseLabel: string;
    isActive: boolean;
}) {
    const [{ limit, offset }] = useLimitOffsetPagination();

    const [result, refetch] = useQuery({
        query: tournamentLayoutsQuery,
        variables: {
            tournamentId,
            tournamentPhase: phase,
            limit,
            offset,
        },
    });

    const [createLayoutRes, createLayout] = useMutation(createTournamentLayoutMutation);

    const { data, fetching } = result;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between pt-4">
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium">{phaseLabel} Layouts</h3>
                    {isActive && (
                        <span className="rounded bg-green-500 px-2 py-1 text-xs text-white">
                            Active
                        </span>
                    )}
                </div>
                <Button
                    onClick={() =>
                        createLayout({
                            input: {
                                tournamentId,
                                tournamentPhase: phase,
                                name: `${phaseLabel} Layout`,
                            },
                        }).then(() => refetch())
                    }
                >
                    Create {phaseLabel} Layout
                </Button>
            </div>

            {createLayoutRes.error && <div>Error: {createLayoutRes.error.message}</div>}

            {data && data.tournamentLayouts && data.tournamentLayouts.items.length > 0 ? (
                <LayoutTable
                    layouts={data.tournamentLayouts.items}
                    isFetching={fetching}
                    tournamentId={tournamentId}
                    phase={phase}
                    onUpdate={() => refetch()}
                />
            ) : fetching ? (
                <div className="text-muted-foreground py-8 text-center">Loading...</div>
            ) : (
                <div className="text-muted-foreground py-8 text-center">
                    No layouts for {phaseLabel.toLowerCase()} phase
                </div>
            )}
        </div>
    );
}

function LayoutTable({
    layouts,
    tournamentId,
    phase,
    onUpdate,
}: {
    layouts: TournamentLayoutsQuery["tournamentLayouts"]["items"];
    isFetching: boolean;
    tournamentId: string;
    phase: TournamentPhase;
    onUpdate: () => void;
}) {
    const navigate = useNavigate();
    const [deleteLayoutId, setDeleteLayoutId] = useState<string | null>(null);
    const [deleteLayoutName, setDeleteLayoutName] = useState<string>("");

    const [createLayoutRes, createLayout] = useMutation(createTournamentLayoutMutation);
    const [deleteLayoutRes, deleteLayout] = useMutation(deleteTournamentLayoutMutation);

    const activeLayoutId = useMemo(() => {
        const now = new Date();

        const publishedLayouts = layouts.filter(layout => {
            if (!layout.publishAt) {
                return false;
            }

            const publishDate = new Date(layout.publishAt);
            const unpublishDate = layout.unpublishAt ? new Date(layout.unpublishAt) : null;

            return publishDate <= now && (!unpublishDate || unpublishDate > now);
        });

        publishedLayouts.sort((a, b) => {
            const dateA = new Date(a.publishAt!);
            const dateB = new Date(b.publishAt!);
            return dateB.getTime() - dateA.getTime();
        });

        return publishedLayouts.length > 0 ? publishedLayouts[0].id : null;
    }, [layouts]);

    const handleDelete = async () => {
        if (deleteLayoutId) {
            await deleteLayout({
                input: {
                    layoutId: deleteLayoutId,
                },
            });
            setDeleteLayoutId(null);
            onUpdate();
        }
    };

    return (
        <>
            {createLayoutRes.error && <div>Error: {createLayoutRes.error.message}</div>}
            {deleteLayoutRes.error && <div>Error: {deleteLayoutRes.error.message}</div>}
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead>Publish at</TableHead>
                        <TableHead>&nbsp;</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {[...layouts]
                        .sort((a, b) => {
                            return (
                                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                            );
                        })
                        .map(layout => {
                            const isActive = layout.id === activeLayoutId;
                            return (
                                <TableRow
                                    key={layout.id}
                                    className="cursor-pointer"
                                    onClick={() => navigate(layout.id)}
                                >
                                    <TableCell>
                                        {layout.name || layout.id.substring(0, 6)}
                                    </TableCell>
                                    <TableCell>
                                        <span
                                            className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium text-white ${isActive ? "bg-green-500" : "bg-gray-500"}`}
                                        >
                                            {isActive ? "Active" : "Inactive"}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        {format(layout.createdAt, "yyyy-MM-dd HH:mm")}
                                    </TableCell>
                                    <TableCell>
                                        {format(layout.updatedAt, "yyyy-MM-dd HH:mm")}
                                    </TableCell>
                                    <TableCell>
                                        {layout.publishAt
                                            ? format(layout.publishAt, "yyyy-MM-dd HH:mm")
                                            : "-"}
                                    </TableCell>
                                    <TableCell className="flex gap-2">
                                        <Button
                                            onClick={ev => {
                                                ev.stopPropagation();
                                                createLayout({
                                                    input: {
                                                        tournamentId,
                                                        tournamentPhase: phase,
                                                        cloneOf: layout.id,
                                                    },
                                                }).then(() => onUpdate());
                                            }}
                                        >
                                            Clone
                                        </Button>
                                        <Button
                                            onClick={ev => {
                                                ev.stopPropagation();
                                                setDeleteLayoutId(layout.id);
                                                setDeleteLayoutName(
                                                    layout.name || layout.id.substring(0, 6)
                                                );
                                            }}
                                            variant="destructive"
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                </TableBody>
            </Table>

            <DestructiveConfirmationAlertDialog
                open={deleteLayoutId !== null}
                onOpenChange={open => {
                    if (!open) setDeleteLayoutId(null);
                }}
                onConfirm={handleDelete}
                title="Delete Layout"
                description={`Are you sure you want to delete "${deleteLayoutName}"? This action cannot be undone.`}
            />
        </>
    );
}
